import {
  Dialog,
  DialogPanel,
  DialogPanelProps,
  Transition,
  TransitionChild,
  TransitionEvents,
} from '@headlessui/react';
import { clsx } from '@payaca/utilities/cvaMerge';
import { FC, Fragment, PropsWithChildren, useRef } from 'react';
import Draggable from 'react-draggable';

export interface ModalCoreProps extends TransitionEvents {
  uId?: string;
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
  disableBackdropClick?: boolean;
  draggable?: boolean;
  panelProps?: DialogPanelProps;
  // Allows multiple modals to be stacked on top of each other
  zIndexLevel?: 1 | 2;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const ModalCore: FC<PropsWithChildren<ModalCoreProps>> = (props) => {
  const {
    className,
    uId,
    children,
    isOpen,
    onClose,
    disableBackdropClick = true,
    draggable,
    panelProps,
    zIndexLevel = 1,
    size,
    ...rest
  } = props;

  const modalRef = useRef(null);

  return (
    <Transition show={isOpen} appear as={Fragment} {...rest}>
      <Dialog onClose={disableBackdropClick ? () => null : () => onClose?.()}>
        {/*Backdrop*/}
        <TransitionChild
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as={Fragment}
        >
          <div
            className={clsx(
              'fixed inset-0 bg-gray-900/30 dark:bg-gray-900/80',
              zIndexLevel === 1 && 'z-modal-backdrop-1',
              zIndexLevel === 2 && 'z-modal-backdrop-2'
            )}
            aria-hidden="true"
          />
        </TransitionChild>

        <TransitionChild
          enter="transition duration-300 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-300 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
          as={Fragment}
        >
          <div
            className={clsx(
              'fixed inset-0 flex w-screen items-center justify-center overflow-x-auto p-4',
              zIndexLevel === 1 && 'z-modal1',
              zIndexLevel === 2 && 'z-modal2'
            )}
          >
            <Draggable
              nodeRef={modalRef}
              disabled={!draggable}
              handle={`.payaca-modal-handle${uId}`}
              // react-draggable preventDefaults touchstart on the handle,
              // which suppresses synthesized clicks on interactive elements
              // inside the modal header on touch devices (P-7713). Cancel
              // them so taps fall through to their own onClick.
              cancel="button,input,select,textarea,a,[role='button']"
              bounds="html"
            >
              <DialogPanel
                data-testid="modal-core-panel"
                ref={modalRef}
                className={clsx(
                  className,
                  size === 'sm' && 'sm:max-w-[42.67rem] sm:w-full',
                  size === 'md' && 'sm:max-w-[56rem] md:w-full',
                  size === 'lg' && 'sm:max-w-[74.67rem] lg:w-full',
                  size === 'xl' && 'sm:max-w-[106.67rem] lg:w-full'
                )}
                {...panelProps}
              >
                {children}
              </DialogPanel>
            </Draggable>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default ModalCore;
